// Client-side occurrence generation for the Cash Flow calendar.
//
// Mirrors backend apps/schedule_payment/services.py: occurrences are computed
// from a schedule's anchor (start_date) + payment_interval, then overlaid with
// one-off exceptions (moved date / skipped / amount override). Used to place
// scheduled payments on their specific date within the displayed window
// (which may span more than one month).
//
// Keep the two in step. Any change to the rules below — the weekday split, the
// holiday move, the penny distribution — has to land in services.py too, or the
// grid and the API will quietly disagree about when money leaves.

import {
  isBankingDay,
  nearestBankingDay,
  nextBankingDay,
  nonBankingReason,
} from "@utils/ontarioBankingDays";

// expense_type → outflow row key.
//
// Rent / Loan / Interest / Payroll each get their own row. EVERYTHING ELSE shares
// one:
// `expense_type` accepts free text when "Other" is chosen, so a clinic with a
// dozen ad-hoc payment names would otherwise render a dozen near-empty rows,
// each holding a single payment. The individual names are not lost — every
// occurrence keeps its own label, which is what the cell's hover breakdown
// lists, and same-day amounts are summed into the one cell.
const NAMED_EXPENSE_TYPES = new Set(["rent", "loan", "interest", "payroll"]);
export const OTHER_ROW_KEY = "other";
const OTHER_ROW_LABEL = "Other payments";

// Display order for the dynamic outflow rows. Derived from NAMED_EXPENSE_TYPES
// rather than restated, so promoting an expense type to its own row also gives it
// a position without a second list to keep in step.
const NAMED_ROW_ORDER = [...NAMED_EXPENSE_TYPES];

// Child-row key and label for the statement side of an account's outflows —
// what a credit-card statement takes OUT of the bank account paying it.
// Namespaced so it can never collide with a raw `expense_type` coming off a
// scheduled payment. Lives here rather than in either grid because both the
// clinic and the entity cash flow render this row, and `sortOutflowRows` below
// already has to know it is not a named expense type.
export const CARD_PAYMENT_KEY = "__card_payment";
export const CARD_PAYMENT_LABEL = "Credit card payment";

/**
 * Order outflow child rows: the named types in their canonical order, then
 * anything else, with "Other payments" ALWAYS last.
 *
 * Without this the order was whichever schedule happened to be encountered first,
 * so the catch-all row could sit between two named ones and its position shifted
 * as data changed. It reads as a total-ish row, and a total belongs at the bottom.
 *
 * Rows are compared by KEY, not label, so renaming a type cannot reshuffle them.
 */
export function sortOutflowRows(rows) {
  const rank = (key) => {
    if (key === OTHER_ROW_KEY) return Number.MAX_SAFE_INTEGER;
    const i = NAMED_ROW_ORDER.indexOf(key);
    // Unnamed keys (the statement "Credit card payment" child) sit after the
    // named rows but still ahead of the catch-all.
    return i === -1 ? NAMED_ROW_ORDER.length : i;
  };
  return [...rows].sort(
    (a, b) => rank(a.key) - rank(b.key) || String(a.label).localeCompare(String(b.label)),
  );
}

// Which outflow row a NetSuite check belongs on — or null for "do not show it".
//
// ONLY a check with a check number (`tranid`) appears on the grid, and it goes
// on the Checks row. A Check record without one is not a cheque: it is a loan
// debit, a bank charge or a transfer that NetSuite happens to store as a Check.
//
// Those were previously kept and filed by memo onto Rent / Loan / Interest /
// "Other payments". That is gone (2026-10-08): a memo is free text, so the row
// an amount landed on was a guess — "LOC payment" read as a Loan, "Deposit from
// Line of credit" was an inflow drawn as an outflow — and those rows are already
// fed properly by the scheduled payments describing the same money.
//
// The backend is asked not to return them either (`has_tranid=true`), so this is
// the second line of defence rather than the only one.
export function checkRowFor(row) {
  if (!String(row?.tranid ?? "").trim()) return null;
  return { key: "checks" };
}

// A NetSuite check and a scheduled payment that describe the SAME money must not
// both be drawn — the grid would show the rent twice. The reconciler already
// suppresses a check it has matched to a schedule (`reconciledTransactionIds`),
// but it only does so once its ledger scan has produced a reference, so a check
// written today against a schedule due today shows alongside it until then.
// This is the cheap same-day guard that covers that gap.
//
// The band is lifted verbatim from the backend matcher
// (`_greedy_match` in apps/schedule_payment/reconciliation.py):
//     band = min(DEFAULT_AMOUNT_TOLERANCE, |amount| * AMOUNT_TOLERANCE_PCT)
// $20 or 2%, whichever is SMALLER — so a $7,386.36 rent allows $20 either way,
// while a $100 charge allows $2 and cannot swallow an unrelated $118 one. Keep
// the two in step: if the backend band changes, change it here, or a payment the
// reconciler pairs will still draw twice on the grid.
const SCHEDULE_MATCH_TOLERANCE = 20;
const SCHEDULE_MATCH_TOLERANCE_PCT = 0.02;

export function scheduleMatchBand(amount) {
  return Math.min(
    SCHEDULE_MATCH_TOLERANCE,
    Math.abs(Number(amount) || 0) * SCHEDULE_MATCH_TOLERANCE_PCT,
  );
}

/**
 * clinic code → { "YYYY-MM-DD": [amount, ...] } for every scheduled occurrence,
 * keyed on its TRUE effective date (`currentDate`), never `displayDate`: a past
 * uncleared payment is rolled forward to today for display, and matching on the
 * rolled date would pair a check with a schedule that was never due that day.
 *
 * The arrays are what make the pairing one-to-one — see `takeScheduleMatch`.
 */
export function scheduledAmountsByDate(occurrencesByClinic) {
  const out = {};
  Object.entries(occurrencesByClinic ?? {}).forEach(([code, rows]) => {
    Object.values(rows ?? {}).forEach((byDate) => {
      Object.values(byDate ?? {}).forEach((list) => {
        (list ?? []).forEach((occ) => {
          const date = occ?.currentDate;
          if (!date) return;
          const perClinic = (out[code] = out[code] || {});
          (perClinic[date] = perClinic[date] || []).push(Number(occ.amount) || 0);
        });
      });
    });
  });
  return out;
}

/**
 * True when `amount` pairs with a scheduled occurrence on `date` — and CONSUMES
 * that occurrence, so two identical checks cannot both hide behind one schedule.
 * The closest amount wins, so an exact match is never spent on a near one.
 *
 * `pool` is mutated: pass a per-render copy from `scheduledAmountsByDate`.
 */
export function takeScheduleMatch(pool, code, date, amount) {
  const amounts = pool?.[code]?.[date];
  if (!amounts || !amounts.length) return false;
  const value = Number(amount) || 0;
  const band = scheduleMatchBand(value);
  let bestIdx = -1;
  let bestDelta = Infinity;
  amounts.forEach((candidate, i) => {
    const delta = Math.abs(candidate - value);
    if (delta <= band && delta < bestDelta) {
      bestDelta = delta;
      bestIdx = i;
    }
  });
  if (bestIdx === -1) return false;
  amounts.splice(bestIdx, 1);
  return true;
}

const INTERVAL_MONTHS = { monthly: 1, quarterly: 3, semi_annually: 6, annually: 12 };
const INTERVAL_DAYS = { weekly: 7, biweekly: 14 };

// Only the week-based intervals can be pinned to weekdays — "every Tue & Thu" has
// no meaning for a monthly schedule, whose anchor is a day-of-month. Mirrors
// SchedulePayment.WEEKDAY_SPLIT_INTERVALS.
export const WEEKDAY_SPLIT_INTERVALS = new Set(["weekly", "biweekly"]);

/**
 * Sorted, de-duplicated payable ISO weekdays (1=Mon … 5=Fri) for a schedule, or
 * [] when it does not use a weekday split.
 *
 * ISO numbering, NOT JavaScript's `getDay()` (Sun=0) — the backend stores ISO so
 * the two sides cannot read each other's encoding as their own. Lenient like the
 * backend's `normalize_weekdays`: this is the read path, so a weekend or a stray
 * string in an old row is dropped rather than thrown, and one bad row cannot
 * break the whole cash-flow grid.
 */
export function splitWeekdays(schedule) {
  if (!WEEKDAY_SPLIT_INTERVALS.has(schedule?.payment_interval)) return [];
  const out = new Set();
  for (const value of schedule?.payment_weekdays ?? []) {
    if (typeof value === "boolean") continue;
    const day = Number(value);
    if (Number.isInteger(day) && day >= 1 && day <= 5) out.add(day);
  }
  return [...out].sort((a, b) => a - b);
}

/**
 * Split `total` into `n` parts summing EXACTLY to it, in whole cents. Leftover
 * pennies go to the earliest weekdays. Mirrors `split_evenly` in services.py.
 */
export function splitEvenly(total, n) {
  const cents = Math.round(Number(total) * 100);
  if (!Number.isFinite(cents) || n < 1) return [];
  const base = Math.floor(cents / n);
  let remainder = cents - base * n;
  return Array.from({ length: n }, () => {
    const extra = remainder > 0 ? 1 : 0;
    remainder -= extra;
    return (base + extra) / 100;
  });
}

/** ISO weekday (1=Mon … 7=Sun) of a Date, from JS's Sun=0 numbering. */
function isoWeekday(date) {
  return ((date.getDay() + 6) % 7) + 1;
}

function parseYMD(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

// Add whole months, clamping the day to the target month's length (the 31st
// becomes the 28th/30th where that day doesn't exist).
function addMonthsClamped(anchor, months) {
  const total = anchor.getMonth() + months;
  const year = anchor.getFullYear() + Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const day = Math.min(anchor.getDate(), lastDay);
  return new Date(year, month, day);
}

// Raw recurring occurrences (pre-exception) from anchor up to and including
// `stop`, as `{ date, amount }`.
//
// The amount rides along because a weekday-split schedule divides its interval
// amount across the selected days, so the base amount is per-occurrence rather
// than simply `schedule.amount`. Mirrors `_base_dates` in services.py.
function baseDates(schedule, stop) {
  const anchor = parseYMD(schedule.start_date);
  if (!anchor) return [];
  const end = parseYMD(schedule.end_date);
  const limit = end && end < stop ? end : stop;
  if (anchor > limit) return [];

  const interval = schedule.payment_interval;
  const amount = Number(schedule.amount);
  const out = [];
  // `movedFrom` is the rule date a holiday move displaced, or null when the
  // occurrence sits where the rule put it. Carried through so the UI can say
  // WHICH payment moved and off what, rather than silently showing a date the
  // user did not choose.
  const at = (date, amt, movedFrom = null) => out.push({ date, amount: amt, movedFrom });

  if (interval === "one_time") {
    at(anchor, amount);
    return out;
  }

  // Every weekday, Mon-Fri. Mirrors `_base_dates` in the backend's
  // services.py — the two generate the same dates, and a change to one without
  // the other makes the grid and reconciliation disagree about what is owed.
  //
  // Weekends are SKIPPED, not moved to the nearest banking day: the adjacent
  // weekday is already a payday for a daily schedule, so moving would put two
  // occurrences on one date and silently double that day's outflow. A statutory
  // holiday is left alone for the same reason, matching the weekly and monthly
  // rules, which do not move either.
  if (interval === "daily") {
    let current = anchor;
    let guard = 0;
    // A higher ceiling than the week-stepping loops below use, because this one
    // advances a DAY at a time: 5000 would stop after ~13 years and quietly drop
    // occurrences inside the visible window for an old schedule. 20000 is ~54
    // years, and the loop is bounded by the window in every real case anyway.
    while (current <= limit && guard < 20000) {
      if (isoWeekday(current) <= 5) at(current, amount);
      current = addDays(current, 1);
      guard += 1;
    }
    return out;
  }

  if (interval === "semi_monthly") {
    let step = 0;
    // guard against runaway loops
    while (step < 1000) {
      const first = addMonthsClamped(anchor, step);
      if (first > limit) break;
      if (first >= anchor) at(first, amount);
      const second = addDays(first, 15);
      if (second >= anchor && second <= limit) at(second, amount);
      step += 1;
    }
    return out;
  }

  if (interval in INTERVAL_DAYS) {
    const days = INTERVAL_DAYS[interval];
    const weekdays = splitWeekdays(schedule);

    // A weekday split REPLACES the anchor-day rule: same weekly/biweekly cadence,
    // but each cycle pays on the selected weekdays. The recurring unit becomes
    // the WEEK, so the cycle is anchored to the Monday of the anchor's week —
    // "every Tue & Thu" must land on Tue and Thu even when the schedule starts on
    // a Wednesday. Days earlier in that first week are skipped.
    if (weekdays.length) {
      const shares = splitEvenly(amount, weekdays.length);
      let weekStart = addDays(anchor, -(isoWeekday(anchor) - 1));
      let guard = 0;
      while (weekStart <= limit && guard < 5000) {
        for (let i = 0; i < weekdays.length; i += 1) {
          const day = addDays(weekStart, weekdays[i] - 1);
          // The RULE date decides whether the occurrence exists; the holiday move
          // only relocates it. Gating on the moved date instead would silently
          // drop an occurrence whose move crossed the anchor or the end date.
          if (day >= anchor && day <= limit) {
            const moved = nearestBankingDay(day);
            const movedISO = toYMD(moved);
            const ruleISO = toYMD(day);
            at(moved, shares[i], movedISO === ruleISO ? null : ruleISO);
          }
        }
        weekStart = addDays(weekStart, days);
        guard += 1;
      }
      return out;
    }

    let current = anchor;
    let guard = 0;
    while (current <= limit && guard < 5000) {
      at(current, amount);
      current = addDays(current, days);
      guard += 1;
    }
    return out;
  }

  const months = INTERVAL_MONTHS[interval];
  if (!months) return out;
  let step = 0;
  while (step < 1000) {
    const current = addMonthsClamped(anchor, months * step);
    if (current > limit) break;
    at(current, amount);
    step += 1;
  }
  return out;
}

/**
 * Occurrences of `schedule` that fall within the inclusive [fromStr, toStr]
 * date range, with one-off exceptions applied. The range can span more than
 * one month (e.g. a 60-day cash-flow window).
 *
 * @param {object} schedule  serialized SchedulePayment (with `exceptions`)
 * @param {string} fromStr   ISO start date, e.g. "2026-07-01"
 * @param {string} toStr     ISO end date (inclusive)
 * @returns {{ date: string, amount: number, originalDate: string, exceptionId: (number|null) }[]}
 */
export function occurrencesInRange(schedule, fromStr, toStr) {
  const from = parseYMD(fromStr);
  const to = parseYMD(toStr);
  if (!from || !to) return [];

  const exceptions = {};
  (schedule.exceptions ?? []).forEach((exc) => {
    exceptions[exc.original_date] = exc;
  });

  const results = [];
  for (const { date: base, amount: baseAmount, movedFrom } of baseDates(schedule, to)) {
    const originalDate = toYMD(base);
    const exc = exceptions[originalDate];
    let effective = base;
    let amount = baseAmount;
    let exceptionId = null;

    if (exc) {
      if (!exc.new_date) continue; // skipped occurrence
      effective = parseYMD(exc.new_date);
      // An override replaces the occurrence's OWN amount, which for a
      // weekday-split schedule is its share of the week, not the whole interval.
      amount = exc.amount != null ? exc.amount : baseAmount;
      exceptionId = exc.id ?? null;
    }

    if (effective >= from && effective <= to) {
      results.push({
        date: toYMD(effective),
        amount: Number(amount),
        originalDate,
        exceptionId,
        movedFrom,
      });
    }
  }
  return results;
}

/** ISO date `days` away from `iso` (negative to go back). Local-calendar, like
 * the rest of this module — never UTC, or a DST boundary shifts the answer. */
function shiftISODays(iso, days) {
  const d = parseYMD(iso);
  if (!d) return iso;
  return toYMD(addDays(d, days));
}

/**
 * Index a `/schedule-payment/reconcile/` response by occurrence, so the grid
 * can ask "did THIS occurrence clear?" in constant time.
 *
 * Keyed on `schedule:original_date` — the same identity the backend's
 * SchedulePaymentException uses — and NOT on the effective date, which moves
 * when an occurrence is rescheduled or rolled forward and so cannot identify
 * anything.
 *
 * @param {object} response  the reconcile endpoint's body
 * @returns {Object} { "12:2026-08-15": { cleared, clearedDate, ... } }
 */
export function buildReconciliationIndex(response) {
  const index = {};
  (response?.items ?? []).forEach((item) => {
    if (item?.schedule == null || !item?.original_date) return;
    index[`${item.schedule}:${item.original_date}`] = {
      cleared: item.cleared === true,
      clearedDate: item.cleared_date ?? null,
      clearedNote: item.cleared_note || "",
      clearedSource: item.cleared_source ?? null,
      // The recorded human decision, when there is one — who decided, when,
      // and off which NetSuite row. Carried onto the occurrence so a cell can
      // say "a person confirmed this" rather than showing it like a guess.
      decision: item.decision ?? null,
      // How sure the match is. The two sides share no key, so a match is a
      // heuristic on amount + date proximity — the grid shows this so "cleared"
      // can read as suggested rather than asserted.
      confidence: item.match?.confidence ?? null,
      matchReference: item.match?.reference ?? null,
      // The NetSuite record behind the match, when there is one. Only the
      // ledger side has a page — a bank-feed row is not an addressable record.
      matchLink: item.match?.ledger?.link ?? null,
      matchAmount: item.match?.amount ?? null,
      matchDate: item.match?.date ?? null,
      amountDelta: item.match?.amount_delta ?? null,
      dayDelta: item.match?.day_delta ?? null,
      // Which evidence carried the match: 'amount' (the figures agreed) or
      // 'memo' (they did not, and the memo said it was this kind of payment
      // anyway — a term loan debiting principal and interest together, say).
      // A tick shown without this reads as "the amounts agreed", which for a
      // memo match is not true.
      matchedBy: item.match?.matched_by ?? null,
      memoCategory: item.match?.memo_category ?? null,
      matchMemo: item.match?.memo ?? null,
      // Candidates that ALMOST matched, when nothing did. Each carries
      // `clears_at_tolerance` — what ?tolerance= would need to be.
      suggestions: item.suggestions ?? [],
      // WHY it has not cleared: 'booked_not_cleared' (NetSuite has the posting,
      // the bank has not taken it), 'amount_mismatch' (the bank HAS a payment
      // of this kind in this window, but it could not be paired — usually a
      // sibling occurrence took it, which points at the schedule rather than at
      // the bank), 'possible_match' (something close exists), or 'no_activity'
      // (neither side has anything). Four different answers needing four
      // different actions, so the grid says which.
      unclearedReason: item.uncleared_reason ?? null,
      bookedNotCleared: item.booked_not_cleared ?? null,
      categoryEvidence: item.category_evidence ?? null,
    };
  });
  return index;
}

/**
 * Aggregate active schedules into grouped cash-flow data, keyed by whatever
 * `keyOf` returns for each schedule.
 *
 * Every expense type becomes its own dynamic row (keyed by the raw expense_type,
 * labelled with expense_type_display) so nothing is dropped.
 *
 * Two callers with two different groupings share this: ClinicCashFlow's clinic cards
 * group by clinic code, the Dens N Dente entity grid groups by bank account —
 * the aggregation itself is identical, only the bucket key differs.
 *
 * RECONCILIATION, when a `reconciliation` index is supplied, changes WHERE an
 * occurrence lands:
 *
 *   cleared                     → stays on its own date. The money has left.
 *   uncleared and already past  → moves to TODAY's cell. The payment did not
 *                                 clear, so the money is still owed, and
 *                                 leaving it on a closed day nobody looks at
 *                                 would understate what is still going out.
 *   uncleared and future        → stays where it is; nothing is late yet.
 *   not mentioned by the API    → stays where it is. Unknown is not uncleared.
 *
 * Same reasoning as the uncleared NetSuite checks that already stack on today
 * (see ClinicCashFlow's `checksUncleared`), and the rolled amounts are tracked
 * separately under `<rowKey>Uncleared` — plus `outflowsUncleared` for the
 * section total — for the same reason: so the cell can flag that part of what
 * it shows has not actually left the bank.
 *
 * The roll lands on today when today is inside the window, and otherwise on the
 * window's LAST day — never on a day the grid is not drawing, which would
 * silently delete the payment from the page. The rolling 30-day view ends
 * yesterday, so the fallback is the common case, not the edge one.
 *
 * @param {object[]} schedules  serialized SchedulePayments (with `exceptions`)
 * @param {string} fromStr      ISO start date of the visible window
 * @param {string} toStr        ISO end date of the visible window (inclusive)
 * @param {(sp: object) => (string|number)} keyOf  bucket key for a schedule
 * @param {{ reconciliation?: Object, today?: string, lookbackDays?: number }} [opts]
 *   `reconciliation` from buildReconciliationIndex; omit to keep the plain
 *   pre-reconciliation behaviour (the pre-roll caller wants exactly that).
 *   `lookbackDays` reaches BEFORE `fromStr` for overdue payments to carry
 *   forward — without it a window that starts at today contains no past at all,
 *   so nothing would ever roll and the overdue money would be invisible on the
 *   default view.
 * @returns {{
 *   values: Object,       // { [key]: { rowKey: {date: amt}, outflows: {date: amt},
 *                         //            <rowKey>Cleared   / outflowsCleared,
 *                         //            <rowKey>Confirmed / outflowsConfirmed,
 *                         //            <rowKey>Uncleared / outflowsUncleared } }
 *   extraRows: Object,    // { [key]: [{ key, label }] } dynamic outflow rows
 *   occurrences: Object,  // { [key]: { rowKey: { date: [occurrence] } } }
 * }}
 */
export function groupScheduleOutflows(schedules, fromStr, toStr, keyOf, opts = {}) {
  const { reconciliation, today, lookbackDays = 0 } = opts;
  // Occurrences are generated from here, not from `fromStr`: an overdue payment
  // due before the window still owes money, and carrying it onto today is the
  // whole point. Ones that are NOT rolled (already cleared, or nothing to
  // carry) are dropped below — they belong to days the grid is not drawing.
  const scanFrom =
    reconciliation && lookbackDays > 0 ? shiftISODays(fromStr, -lookbackDays) : fromStr;
  // Where an overdue payment lands. Today when today is on the page — but the
  // cash-flow window does not always include it (the rolling 30-day view ends
  // yesterday), and refusing to roll in that case hid every overdue payment's
  // marker on exactly the views most likely to be looked at. So it falls back
  // to the LAST visible day: the closest cell to today that the grid actually
  // draws, which is where "still owed as of the end of this window" belongs.
  //
  // A window entirely in the FUTURE gets no target at all — nothing in it is
  // overdue yet, and there is no past to carry forward.
  const rollTarget =
    !reconciliation || !today
      ? null
      : today >= fromStr && today <= toStr
        ? today
        : today > toStr
          ? toStr
          : null;
  const canRoll = !!rollTarget;
  const values = {};
  const extra = {};
  const occurrences = {};

  (schedules ?? []).forEach((sp) => {
    // Status is deliberately NOT consulted. `end_date` is what stops a
    // cancelled series — occurrencesInRange already honours it — so a cancelled
    // schedule contributes exactly its occurrences up to the day it stopped and
    // nothing after. Gating on status here is what used to make cancelling
    // retroactive, wiping months of already-paid rent off the grid.
    const occ = occurrencesInRange(sp, scanFrom, toStr);
    if (!occ.length) return;

    // `label` stays the schedule's OWN name even when it lands in the shared
    // "Other payments" row — that is what makes the hover breakdown useful.
    const label = sp.expense_type_display || sp.expense_type;
    const isNamed = NAMED_EXPENSE_TYPES.has(sp.expense_type);
    const rowKey = isNamed ? sp.expense_type : OTHER_ROW_KEY;
    const rowLabel = isNamed ? label : OTHER_ROW_LABEL;
    const code = keyOf(sp);
    if (code == null) return;

    // How many payments this schedule's interval amount is split across, per
    // cycle — 1 for everything but a weekday-split weekly/biweekly. Surfaced on
    // the occurrence so an editor can say that skipping ONE of them drops that
    // day's share alone: the others keep their own amounts rather than growing
    // to absorb it.
    const splitOf = splitWeekdays(sp).length || 1;

    const bucket = (values[code] = values[code] || {});
    const occBucket = (occurrences[code] = occurrences[code] || {});
    occ.forEach(({ date, amount, originalDate, exceptionId }) => {
      const rec = reconciliation?.[`${sp.id}:${originalDate}`] ?? null;
      const cleared = rec?.cleared === true;
      // Past AND known-uncleared is the one case that moves. `date` (the
      // effective date, after any exception) is what decides — that is when the
      // money was meant to leave.
      //
      // `rec &&` is load-bearing: an occurrence the reconciler said nothing
      // about is UNKNOWN, not uncleared. Without it, a reconcile call that is
      // still in flight or has failed reads as "nothing cleared" and every past
      // payment in the window piles onto today — the grid would look alarming
      // for a reason that is purely a network state. Occurrences outside the
      // backend's lookback are the same case, and equally must stay put.
      const rolled = canRoll && !!rec && !cleared && date < rollTarget;
      const placedDate = rolled ? rollTarget : date;
      // Anything from the lookback that is NOT being carried forward has no
      // cell to sit in — its own date is off the left edge of the grid. Adding
      // it to `outflows` anyway would inflate a total nobody can see the source
      // of, so it is dropped.
      if (placedDate < fromStr) return;

      bucket[rowKey] = bucket[rowKey] || {};
      bucket[rowKey][placedDate] = (bucket[rowKey][placedDate] || 0) + amount;
      bucket.outflows = bucket.outflows || {};
      bucket.outflows[placedDate] = (bucket.outflows[placedDate] || 0) + amount;
      if (cleared) {
        // The cleared half of the same bookkeeping. Without it a cell can only
        // say how much of its total is OVERDUE; this lets it also say how much
        // has genuinely left the bank, which is the difference between a cell
        // the user still has to act on and one they can stop thinking about.
        const clearedKey = `${rowKey}Cleared`;
        bucket[clearedKey] = bucket[clearedKey] || {};
        bucket[clearedKey][placedDate] = (bucket[clearedKey][placedDate] || 0) + amount;
        bucket.outflowsCleared = bucket.outflowsCleared || {};
        bucket.outflowsCleared[placedDate] = (bucket.outflowsCleared[placedDate] || 0) + amount;
        // Tracked apart AGAIN, for the part somebody has actually confirmed.
        // Without this a cell cannot tell a recorded decision from the matcher's
        // guess, and both drew the same emerald tick — so a guess read as a
        // settled fact to anyone not hovering the cell. The distinction is the
        // whole reason the confirm step exists.
        if (rec?.decision) {
          const confirmedKey = `${rowKey}Confirmed`;
          bucket[confirmedKey] = bucket[confirmedKey] || {};
          bucket[confirmedKey][placedDate] = (bucket[confirmedKey][placedDate] || 0) + amount;
          bucket.outflowsConfirmed = bucket.outflowsConfirmed || {};
          bucket.outflowsConfirmed[placedDate] =
            (bucket.outflowsConfirmed[placedDate] || 0) + amount;
        }
      }
      if (rolled) {
        // Tracked apart from the total so the cell can flag that some of what
        // it shows has not actually left the bank. `<rowKey>Uncleared` follows
        // the convention `checksUncleared` already set, so the card can look up
        // the flag for ANY outflow row by name instead of special-casing each;
        // the section total gets its own so the Outflows row can flag too.
        const unclearedKey = `${rowKey}Uncleared`;
        bucket[unclearedKey] = bucket[unclearedKey] || {};
        bucket[unclearedKey][placedDate] = (bucket[unclearedKey][placedDate] || 0) + amount;
        bucket.outflowsUncleared = bucket.outflowsUncleared || {};
        bucket.outflowsUncleared[placedDate] = (bucket.outflowsUncleared[placedDate] || 0) + amount;
      }

      occBucket[rowKey] = occBucket[rowKey] || {};
      occBucket[rowKey][placedDate] = occBucket[rowKey][placedDate] || [];
      occBucket[rowKey][placedDate].push({
        scheduleId: sp.id,
        clinic: code,
        label,
        amount,
        originalDate,
        exceptionId,
        splitOf,
        // The occurrence's TRUE effective date — deliberately NOT `placedDate`.
        // MoveOccurrenceModal prefills from this and posts `new_date:
        // currentDate` when the user changes nothing, so a rolled-forward
        // occurrence reporting today here would persist an exception moving the
        // payment to today the moment someone opened the modal and saved. A
        // display roll must never become stored data.
        currentDate: date,
        // Where it sits in the grid, which for a rolled occurrence is today.
        displayDate: placedDate,
        // Reconciliation, for the cell's breakdown panel.
        cleared,
        clearedDate: rec?.clearedDate ?? null,
        clearedNote: rec?.clearedNote ?? "",
        clearedSource: rec?.clearedSource ?? null,
        decision: rec?.decision ?? null,
        confidence: rec?.confidence ?? null,
        // The NetSuite row that explains the payment — "ledger:9741792/0" or
        // "bank_feed:85621". Surfaced so a cell can name its evidence, which is
        // the difference between a mark a person can check and one they have to
        // take on faith.
        matchReference: rec?.matchReference ?? null,
        matchAmount: rec?.matchAmount ?? null,
        matchLink: rec?.matchLink ?? null,
        matchDate: rec?.matchDate ?? null,
        suggestions: rec?.suggestions ?? [],
        matchedBy: rec?.matchedBy ?? null,
        memoCategory: rec?.memoCategory ?? null,
        matchMemo: rec?.matchMemo ?? null,
        unclearedReason: rec?.unclearedReason ?? null,
        bookedNotCleared: rec?.bookedNotCleared ?? null,
        categoryEvidence: rec?.categoryEvidence ?? null,
        rolledForwardFrom: rolled ? date : null,
        // Drives the existing amber "Uncleared" chip in the breakdown line —
        // the same one the uncleared checks use, so the two read alike.
        uncleared: rolled,
      });
    });

    // Register the row so the card renders it. Rows are dynamic, so only types
    // that actually have a schedule appear.
    const seen = (extra[code] = extra[code] || {});
    if (!seen[rowKey]) {
      seen[rowKey] = rowLabel;
    }
  });

  const extraRows = {};
  Object.entries(extra).forEach(([code, seen]) => {
    extraRows[code] = sortOutflowRows(Object.entries(seen).map(([key, label]) => ({ key, label })));
  });

  return { values, extraRows, occurrences };
}

/**
 * Occurrences sitting on a day money cannot move — a weekend or an Ontario bank
 * closure — that nothing has deliberately put there.
 *
 * The grid computes these dates from the recurring rule, so a monthly rent
 * anchored to the 15th lands on a Saturday twice a year and the cell claims cash
 * leaves on a day no bank will clear it. Each one returned here is a move to
 * persist as a one-off SchedulePaymentException, keyed on `originalDate` so the
 * rule — and every other occurrence of the series — is untouched.
 *
 * FORWARD only (`nextBankingDay`, not the display rule's `nearestBankingDay`):
 * this changes when money actually leaves, and paying early is a different
 * decision from paying on the next day the bank is open.
 *
 * Occurrences that ALREADY carry an exception are skipped — that date was chosen
 * by a person, and a hand-set Saturday is a deliberate one to leave alone.
 *
 * @param {Object} occurrencesByKey  { [bucket]: { [rowKey]: { [date]: occurrence[] } } }
 *   — `occurrencesByClinic` / `occurrencesByAccount` as returned above.
 * @returns {{ key: string, scheduleId: number, originalDate: string,
 *             currentDate: string, newDate: string, label: string,
 *             amount: number, reason: string }[]}
 */
export function collectNonBankingMoves(occurrencesByKey) {
  // Keyed, not pushed: the same schedule occurrence can be reached through more
  // than one bucket, and two POSTs for one occurrence would collide on the
  // (schedule, original_date) unique constraint.
  const moves = new Map();

  Object.values(occurrencesByKey ?? {}).forEach((byRow) => {
    Object.values(byRow ?? {}).forEach((byDate) => {
      Object.entries(byDate ?? {}).forEach(([date, items]) => {
        const day = parseYMD(date);
        if (!day || isBankingDay(day)) return;
        const newDate = toYMD(nextBankingDay(day));
        // "a Saturday" / "Canada Day" — the confirmation names WHY a payment is
        // being moved, so the user is agreeing to something they can check.
        const reason = nonBankingReason(day);
        (items ?? []).forEach((item) => {
          if (item?.exceptionId != null) return;
          // A rolled-forward occurrence is sitting on today because it never
          // cleared, not because the rule put it there. Its grid date is a
          // display decision, so proposing a stored move off that date would
          // rewrite the schedule to match a presentation choice — and if today
          // happens to be a Saturday, it would do it to every overdue payment
          // at once.
          if (item?.rolledForwardFrom) return;
          if (item?.scheduleId == null || !item?.originalDate) return;
          const key = `${item.scheduleId}:${item.originalDate}`;
          if (moves.has(key)) return;
          moves.set(key, {
            key,
            scheduleId: item.scheduleId,
            originalDate: item.originalDate,
            currentDate: date,
            newDate,
            reason,
            label: item.label,
            amount: item.amount,
          });
        });
      });
    });
  });

  return [...moves.values()];
}

/**
 * Per-CLINIC grouping, consumed by ClinicCashFlowCard via ClinicCashFlow.jsx.
 * `sp.clinic` is the clinic CODE, still served read-only by the schedule-payment
 * serializer after the model moved to a bank_account FK.
 */
export function buildScheduleOutflows(schedules, fromStr, toStr, opts = {}) {
  const { values, extraRows, occurrences } = groupScheduleOutflows(
    schedules,
    fromStr,
    toStr,
    (sp) => sp.clinic,
    opts,
  );
  return {
    valuesByClinic: values,
    extraRowsByClinic: extraRows,
    occurrencesByClinic: occurrences,
  };
}

/**
 * Per-BANK-ACCOUNT grouping, consumed by EntityCashFlowTable. Entity-level
 * accounts have no clinic, so the clinic grouping above would bucket them all
 * under `undefined` — the account id is the only key that separates them.
 */
export function buildScheduleOutflowsByAccount(schedules, fromStr, toStr, opts = {}) {
  const { values, extraRows, occurrences } = groupScheduleOutflows(
    schedules,
    fromStr,
    toStr,
    (sp) => sp.bank_account,
    opts,
  );
  return {
    valuesByAccount: values,
    extraRowsByAccount: extraRows,
    occurrencesByAccount: occurrences,
  };
}
